import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Check, Star, Sparkles } from 'lucide-react';

const PackagesSection = ({ scrollToContact }) => {
  const { t } = useLanguage();

  const packages = [
    {
      key: 'starter',
      icon: Star,
      featured: false,
    },
    {
      key: 'professional',
      icon: Sparkles,
      featured: true,
    },
    {
      key: 'enterprise',
      icon: Star,
      featured: false,
    },
  ];

  return (
    <section 
      id="packages" 
      className="section-padding bg-[#0a0a0a] relative overflow-hidden"
      data-testid="packages-section"
    >
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
      <div className="absolute right-0 top-1/3 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            data-testid="packages-title"
          >
            {t(translations.packages.sectionTitle)}
          </h2>
          <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto">
            {t(translations.packages.sectionSubtitle)}
          </p>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {packages.map((pkg, index) => {
            const pkgData = translations.packages[pkg.key];
            const features = t(pkgData.features);
            const Icon = pkg.icon;

            return (
              <div 
                key={pkg.key}
                className={`package-card ${pkg.featured ? 'featured' : ''}`}
                data-testid={`package-${pkg.key}`}
              >
                {pkg.featured && (
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500 text-black">
                      Popular
                    </span>
                  </div>
                )}

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      pkg.featured ? 'bg-emerald-500/20' : 'bg-white/10'
                    }`}>
                      <Icon className={`w-5 h-5 ${pkg.featured ? 'text-emerald-500' : 'text-white/60'}`} />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {t(pkgData.title)}
                    </h3>
                  </div>

                  <p className="text-white/50 text-sm mb-6">
                    {t(pkgData.description)}
                  </p>

                  <ul className="space-y-3 mb-8">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          pkg.featured ? 'text-emerald-500' : 'text-white/40'
                        }`} />
                        <span className="text-sm text-white/70">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => scrollToContact(t(pkgData.title))}
                  className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.featured 
                      ? 'bg-emerald-500 text-black hover:bg-emerald-400' 
                      : 'border border-white/20 text-white hover:bg-white/10'
                  }`}
                  data-testid={`package-${pkg.key}-cta`}
                >
                  {t(translations.packages.cta)}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
